import type { SojournDetailDto } from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { formatRange, roomLabel } from '../../shared/format.js';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Button,
  CollapsibleCard,
  ConfirmButton,
  Dialog,
  ErrorAlert,
  Field,
  LoadingBlock,
  Select,
  TextArea,
} from '../../shared/ui/index.js';
import { useAllRooms } from '../houses/houseHooks.js';
import { DateRangeCalendar, type PartialDateRange } from './DateRangeCalendar.js';
import { useCreatePlanLock, usePlanLocks, useRemovePlanLock } from './sojournHooks.js';

/**
 * Manual overrides.
 *
 * Every lock carries a written reason, because the value of an override is not
 * the placement itself but being able to answer "why is she in that room?"
 * three weeks later.
 */
export function PlanLocks({ sojourn }: { sojourn: SojournDetailDto }): ReactElement {
  const t = useTranslation();
  const locks = usePlanLocks(sojourn.id);
  const removeLock = useRemovePlanLock(sojourn.id);
  const [adding, setAdding] = useState(false);

  return (
    <>
      <CollapsibleCard
        title={t.planLocks.title}
        subtitle={t.planLocks.subtitle}
        error={locks.error ?? removeLock.error}
        actions={
          <Button
            variant="secondary"
            small
            disabled={sojourn.guests.length === 0 || sojourn.houses.length === 0}
            onClick={() => setAdding(true)}
          >
            {t.planLocks.addLock}
          </Button>
        }
      >
        {locks.isPending ? <LoadingBlock label={t.planLocks.loadingLocks} /> : null}

        {locks.data?.length === 0 ? (
          <p className="field__hint">{t.planLocks.emptyDescription}</p>
        ) : null}

        {locks.data !== undefined && locks.data.length > 0 ? (
          <ul className="data-list">
            {locks.data.map((lock) => (
              <li key={lock.id} className="list-row">
                <div className="list-row__main">
                  <span className="list-row__title" title={lock.bedName ?? undefined}>
                    {lock.guestName} → {lock.houseName} · {lock.roomName}
                    {lock.bedType === null || lock.bedOption === null
                      ? ''
                      : ` · ${bedKindLabel(t, lock.bedType, lock.bedOption)}`}
                  </span>
                  <span className="list-row__meta">
                    {lock.range === null ? t.planLocks.wholeStay : formatRange(lock.range)} —{' '}
                    {lock.justification}
                  </span>
                </div>
                <ConfirmButton
                  small
                  label={t.common.remove}
                  onConfirm={() => removeLock.mutate(lock.id)}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </CollapsibleCard>

      {adding ? <AddLockDialog sojourn={sojourn} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function AddLockDialog({
  sojourn,
  onClose,
}: {
  sojourn: SojournDetailDto;
  onClose: () => void;
}): ReactElement {
  const t = useTranslation();
  const createLock = useCreatePlanLock(sojourn.id);
  const rooms = useAllRooms();
  const fieldError = useFieldErrors(createLock.error);

  const [guestId, setGuestId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [justification, setJustification] = useState('');
  const [limitToDates, setLimitToDates] = useState(false);
  const [range, setRange] = useState<PartialDateRange>({ start: null, end: null });

  // Only rooms from houses actually taking part can be locked to.
  const participating = new Set(sojourn.houses.map((house) => house.houseId));
  const lockableRooms = (rooms.data ?? []).filter((room) => participating.has(room.houseId));

  const lockedRange =
    limitToDates && range.start !== null && range.end !== null
      ? { start: range.start, end: range.end }
      : null;
  // A checked "only for part of the stay" with no complete range picked yet
  // is not something the server should ever see.
  const canSubmit = !limitToDates || lockedRange !== null;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    createLock.mutate(
      { guestId, roomId, bedId: null, range: lockedRange, justification },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog title={t.planLocks.dialogTitle} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={createLock.error} />

        <Field label={t.planLocks.guestLabel} error={fieldError('guestId')}>
          {(props) => (
            <Select
              {...props}
              value={guestId}
              onChange={(event) => setGuestId(event.target.value)}
              required
            >
              <option value="">{t.planLocks.chooseGuest}</option>
              {sojourn.guests.map((guest) => (
                <option key={guest.guestId} value={guest.guestId}>
                  {guest.guestName}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t.planLocks.roomLabel} error={fieldError('roomId')}>
          {(props) => (
            <Select
              {...props}
              value={roomId}
              onChange={(event) => setRoomId(event.target.value)}
              required
            >
              <option value="">{t.planLocks.chooseRoom}</option>
              {lockableRooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {roomLabel(room)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={limitToDates}
            onChange={(event) => setLimitToDates(event.target.checked)}
          />
          {t.planLocks.onlyPartOfStay}
        </label>

        {limitToDates ? (
          <div className="panel stack stack--tight">
            <DateRangeCalendar
              bounds={{ start: sojourn.startDate, end: sojourn.endDate }}
              value={range}
              onChange={setRange}
            />
          </div>
        ) : null}

        <Field
          label={t.planLocks.whyLabel}
          hint={t.planLocks.whyHint}
          error={fieldError('justification')}
        >
          {(props) => (
            <TextArea
              {...props}
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              required
            />
          )}
        </Field>

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" loading={createLock.isPending} disabled={!canSubmit}>
            {t.planLocks.lockPlacement}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
